import React, { useMemo, useCallback, useEffect, useRef, useContext, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import { startOfDay } from 'date-fns'
import { withTranslation } from 'react-i18next';

import { makeGetMessagesByTypeAndAssociationId, makeGetNonSystemMessagesByTypeAndAssociationId, makeGetEarliestMessagesByTypeAndAssociationId, makeGetLastSelfMessage } from 'selectors/messages';
import { makeGetMessageNotificationsByAssociationId } from 'selectors/notifications';
import { makeGetCurrentUserId } from 'selectors/users';
import { makeGetFetchedState, makeGetLoadingState, makeGetHasAfter, makeGetHasBefore } from 'selectors/global';

import { fetchMessages, fetchMessage, seeMessage } from 'actions/messages';
import { readNotifications } from 'actions/notifications';

import { getFetchKey } from 'utils/message';
import { serializeElement } from 'utils/html';
import { useWakeUp } from 'utils/hooks';

import { DocumentFocusContext } from 'context/DocumentFocusContext';
import { DiscussionProvider } from 'context/DiscussionContext';

import DiscussionNotificationsBar from 'components/discussion-notifications-bar';
import DiscussionDayGroup from 'components/discussion-day-group';
import NotificationsReader from 'components/NotificationsReader';
import MessageSeener from 'components/MessageSeener';

import IconChatBubble from 'icons/icon-chat-bubble';

import MessagePlaceholder from 'components/placeholders/message.placeholder';

const MSG_LIMIT = 25;
// const SCROLL_LOAD_MORE_THRESHOLD = 1000;
const SCROLL_LOAD_MORE_THRESHOLD = 300;
const SCROLL_TO_MESSAGE_OFFSET = 34;
const SCROLL_TO_BOTTOM_THRESHOLD = 100;
const AUTOFETCHER_INTERVAL = 1000 * 60 * 3; // 3 mins

const DiscussionMessages = ({ id, type, projectId, filePreviewPath, focused, emptyComponent, emptyText, canCreateTask, dateFormat, gifSize, users, urlPreviewEnabled, systemAsAction, botUseUserName, controlDeleteText, pinnedMessage, pinnedMessageBy, noDivider, messages, loading, fetched, hasBefore, hasAfter, earliestMessage, lastSelfMessage, startFromMessage, notifications, currentUserId, canSendAsEmail, canLinkMessage, onNotFound, onClick, onThreadActivate, onPinMessage, provideController, t }) => {
    const documentFocusContext = useContext(DocumentFocusContext);
    const lastScrollPos = useRef(0);
    const scrollElement = useRef(null);
    const discussionBody = useRef(null);
    const pinToMessage = useRef(null);
    const fetchInterval = useRef(null);
    const freezePosition = useRef(null);
    const endReached = useRef(null);
    const fetchedForMessage = useRef(false);
    const scrollHeightBeforeUpdate = useRef(0);
    const scrollTopBeforeUpdate = useRef(0);
    const scrollFromBottomBeforeUpdate = useRef(0);
    const messageCtrls = useRef([]);

    const lastMessage = useMemo(() => {
        if (!messages?.length) {
            return null;
        }

        return messages[0];
    }, [messages]);

    const scrollTo = useCallback(scrollTo => {
        if (!scrollElement.current) {
            return;
        }

        scrollElement.current.scrollTop = scrollTo;

        if (freezePosition.current !== null) {
            freezePosition.current = scrollTo;
        }
    }, [scrollElement, freezePosition]);

    const scrollToTop = useCallback(() => {
        scrollTo(0);
    }, [scrollTo]);

    const scrollToBottom = useCallback(() => {
        if (!scrollElement.current) {
            return;
        }

        scrollTo(scrollElement.current.scrollHeight);
    }, [scrollElement, scrollTo]);

    const scrollToMessage = useCallback((messageId, ifNeeded) => {
        if (!scrollElement.current) {
            return;
        }

        const element = scrollElement.current.querySelector(`[data-message-id="${messageId}"]`);

        if (!element) {
            return;
        }

        const scrollTop = scrollElement.current.scrollTop;
        const scrollRect = scrollElement.current.getBoundingClientRect();
        const messageRect = element.getBoundingClientRect();

        if (ifNeeded) {
            if (messageRect.bottom < scrollRect.bottom) {
                return;
            }
        }

        scrollTo(scrollTop + messageRect.top - scrollRect.top - SCROLL_TO_MESSAGE_OFFSET);
    }, [scrollElement, scrollTo]);

    const freezeScroll = useCallback(() => {
        if (!scrollElement.current) {
            return;
        }

        try {
            scrollElement.current.style.paddingRight = scrollElement.current.parentNode.clientWidth - scrollElement.current.clientWidth + `px`;
        } catch (e) {}

        scrollElement.current.style.overflowY = 'hidden';

        freezePosition.current = scrollElement.current.scrollTop;
    }, [scrollElement, freezePosition]);

    const unfreezeScroll = useCallback(() => {
        if (!scrollElement.current || freezePosition.current === null) {
            return;
        }

        scrollElement.current.style.paddingRight = 0;
        scrollElement.current.style.overflowY = 'auto';
        freezePosition.current = null;
    }, [scrollElement, freezePosition]);

    const doFetchMessages = useCallback(({ before_date, after_date, before_id, after_id } = {}, autoFetch) => {
        if (!id) {
            return Promise.reject();
        }

        return fetchMessages({
            [getFetchKey(type)]: id,
            limit: MSG_LIMIT,
            before_date,
            after_date,
            before_id,
            after_id
        });
    }, [id, type]);

    const loadMessagesBefore = useCallback(() => {
        if (!loading && hasBefore) {
            doFetchMessages({
                before_date: earliestMessage?.created_at
            });
        }
    }, [loading, hasBefore, earliestMessage, doFetchMessages]);

    const loadMessagesAfter = useCallback(() => {
        if (!loading && hasAfter) {
            doFetchMessages({
                after_date: lastMessage?.created_at
            });
        }
    }, [loading, hasAfter, lastMessage, doFetchMessages]);

    const loadMoreMessages = useCallback(({ before, after }) => {
        if (before) {
            loadMessagesBefore();
        }

        if (after) {
            loadMessagesAfter();
        }
    }, [loadMessagesBefore, loadMessagesAfter]);

    const shouldLoadMore = useCallback(checkDirection => {
        if (!id || !scrollElement.current) {
            return false;
        }

        const elementHeight = scrollElement.current.clientHeight;
        const scrollHeight = scrollElement.current.scrollHeight;
        const newScrollPos = scrollElement.current.scrollTop;
        const scrollUp = lastScrollPos.current > newScrollPos;
        const scrollDown = lastScrollPos.current < newScrollPos;

        lastScrollPos.current = newScrollPos;

        return {
            before: elementHeight && hasBefore && newScrollPos < SCROLL_LOAD_MORE_THRESHOLD && (!checkDirection || (checkDirection && scrollUp)),
            after: elementHeight && hasAfter && scrollHeight - (newScrollPos + elementHeight) < SCROLL_LOAD_MORE_THRESHOLD && (!checkDirection || (checkDirection && scrollDown))
        }
    }, [id, scrollElement.current, lastScrollPos.current, hasBefore, hasAfter]);

    const markNotificationsAsRead = useCallback(notifications => {
        readNotifications(notifications.map(notification => notification.id));
    }, []);

    const loadedMessagesNotifications = useMemo(() => {
        return notifications.filter(notification => notification && messages.some(message => message.id === notification.association.message_id));
    }, [notifications, messages]);

    const loadForMessage = useCallback(async messageId => {
        fetchedForMessage.current = true;
        pinToMessage.current = messageId;

        const messageFromStore = messages.find(message => message.id === messageId);
        const message = messageFromStore || await fetchMessage(messageId);

        scrollToMessage(messageId);

        await doFetchMessages({ before_date: message.created_at });
        scrollToMessage(messageId);

        await doFetchMessages({ after_date: message.created_at });
        scrollToMessage(messageId);

        setTimeout(() => {
            pinToMessage.current = null;
        }, 10000);
    }, [fetchedForMessage.current, pinToMessage.current, messages, scrollToMessage, doFetchMessages]);

    const attemptFetch = useCallback(force => {
        if (!force && (!id || fetched || loading || pinToMessage)) {
            return;
        }

        if (startFromMessage && !fetchedForMessage.current) {
            loadForMessage(startFromMessage);
            return;
        }

        doFetchMessages()
            .catch(error => {
                if (error === 'Chat not found') {
                    onNotFound && onNotFound();
                }
            });
    }, [id, fetched, loading, startFromMessage, pinToMessage.current, fetchedForMessage.current, loadForMessage, doFetchMessages, onNotFound]);

    const startAutoFetcher = useCallback(() => {
        if (fetchInterval.current || !endReached) {
            return;
        }

        fetchInterval.current = setInterval(() => doFetchMessages({}, true), AUTOFETCHER_INTERVAL);
    }, [fetchInterval.current, endReached.current, doFetchMessages]);

    const stopAutoFetcher = useCallback(() => {
        if (!fetchInterval.current) {
            return;
        }

        clearInterval(fetchInterval.current);
        fetchInterval.current = null;
    }, [fetchInterval.current]);

    const messageGroups = useMemo(() => {
        const reversedMessages = [...(messages || [])].reverse();
        const groups = [];

        reversedMessages.forEach(message => {
            const day = startOfDay(new Date(message.created_at)).getTime();
            let selectedGroup = groups.find(group => group.day === day);

            if (!selectedGroup) {
                selectedGroup = {
                    day,
                    items: []
                };

                groups.push(selectedGroup);
            }

            selectedGroup.items.push(message);
        });

        return groups;
    }, [messages]);

    const emptyElement = useMemo(() => {
        return emptyComponent || (
            <div className="discussion-add-new">
                <IconChatBubble />

                <p>{emptyText || t('empty.discussion')}</p>
            </div>
        )
    }, [emptyComponent, emptyText, t]);

    scrollHeightBeforeUpdate.current = scrollElement.current?.scrollHeight || 0;
    scrollTopBeforeUpdate.current = scrollElement.current?.scrollTop || 0;
    scrollFromBottomBeforeUpdate.current = !scrollElement.current ? 0 : scrollElement.current.scrollHeight - (scrollElement.current.scrollTop + scrollElement.current.offsetHeight);

    const shouldScrollBottom = useCallback(() => {
        if (!scrollElement.current || pinToMessage.current) {
            return false;
        }

        // return scrollHeightBeforeUpdate.current - (scrollElement.current.scrollTop + scrollElement.current.offsetHeight) <= SCROLL_TO_BOTTOM_THRESHOLD;
        return scrollFromBottomBeforeUpdate.current <= SCROLL_TO_BOTTOM_THRESHOLD;
    }, [pinToMessage.current, scrollElement.current]);

    const handleDiscussionScroll = useCallback(event => {
        if (freezePosition.current !== null) {
            scrollElement.current.scrollTop = freezePosition.current;
            return;
        }

        loadMoreMessages(shouldLoadMore(true));
    }, [freezePosition.current, scrollElement.current, loadMoreMessages, shouldLoadMore]);

    const handleNotificationsBarClick = useCallback(() => {
        scrollToTop();
    }, [scrollToTop]);

    const handleNotificationsBarClose = useCallback(() => {
        markNotificationsAsRead(notifications);
    }, [markNotificationsAsRead, notifications])

    const handleDiscussionCopy = useCallback(event => {
        const serializedContent = serializeElement(document.getSelection().getRangeAt(0).cloneContents());
        event.clipboardData.setData('text/plain', serializedContent);
        event.preventDefault();
    }, []);

    const handleDiscussionClick = useCallback(event => {
        try {
            if (event.target?.closest('.message.editing') || !discussionBody.current.contains(event.target)) {
                return;
            }

            onClick();
        } catch (e) {
            console.error(e);
        }
    }, [discussionBody.current, onClick]);

    const handleMessageEditStart = useCallback(messageId => {
        scrollToMessage(messageId, true);
    }, [scrollToMessage]);

    const registerMessageCtrl = useCallback((id, ctrl) => {
        messageCtrls.current = [...messageCtrls.current, ({ id, ctrl })];

        return () => {
            messageCtrls.current = messageCtrls.current.filter(c => c.id !== id);
        }
    }, [messageCtrls]);

    useEffect(() => {
        attemptFetch(true);
        scrollToBottom();
    }, [id]);

    useLayoutEffect(() => {
        if (freezePosition.current === null) {
            freezeScroll();
        }

        if (shouldScrollBottom()) {
            scrollToBottom();
        }

        requestAnimationFrame(() => {
            if (scrollElement.current) {
                if (pinToMessage.current) {
                    scrollToMessage(pinToMessage.current);
                } else {
                    scrollTo(scrollTopBeforeUpdate.current + (scrollElement.current.scrollHeight - scrollHeightBeforeUpdate.current));
                }
            }

            unfreezeScroll();

            if (fetched) {
                requestAnimationFrame(() => {
                    loadMoreMessages(shouldLoadMore());
                })
            }
        });
    }, [messages]);

    useEffect(() => {
        if (provideController) {
            provideController.current = {
                scrollToBottom: () => scrollToBottom(),
                shouldScrollBottom: () => shouldScrollBottom(),
                editLastSelfMessage: () => {
                    const entry = messageCtrls.current.find(entry => entry.id === lastSelfMessage?.id);

                    if (entry?.ctrl) {
                        entry.ctrl.edit();
                    }
                }
            };
        }

        return () => {
            if (provideController) {
                provideController.current = null;
            }
        }
    }, [shouldScrollBottom, lastSelfMessage?.id]);

    useEffect(() => {
        startAutoFetcher();

        return () => {
            stopAutoFetcher();
        }
    }, [id]);

    useWakeUp(() => {
        doFetchMessages();
    });

    if (!messageGroups.length) {
        if (!fetched && id) {
            return (
                <div className="discussion-body">
                    <div className="discussion-scrollable nice-scroll">
                        <div className="discussion-scrollable-inner">
                            <MessagePlaceholder key={0} />

                            <MessagePlaceholder key={1} />

                            <MessagePlaceholder key={2} />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="discussion-body empty">
                { emptyElement }
            </div>
        );
    }

    return (
        <div className="discussion-body" ref={discussionBody} onCopy={handleDiscussionCopy} onClick={handleDiscussionClick}>
            <NotificationsReader enabled={documentFocusContext.focused && focused} notifications={loadedMessagesNotifications.map(({ id }) => id)} />

            <MessageSeener enabled={documentFocusContext.focused && focused} messages={messages} />

            <DiscussionNotificationsBar notificationsCount={notifications.length} onClick={handleNotificationsBarClick} onClose={handleNotificationsBarClose} />

            <div className="discussion-scrollable nice-scroll" ref={scrollElement} onScroll={handleDiscussionScroll}>
                <div className="discussion-scrollable-inner">
                    <DiscussionProvider
                        discussionId={id}
                        currentUserId={currentUserId}
                        filePreviewPath={filePreviewPath}
                        canCreateTask={canCreateTask}
                        dateFormat={dateFormat}
                        projectId={projectId}
                        urlPreviewEnabled={urlPreviewEnabled}
                        gifSize={gifSize}
                        users={users}
                        systemAsAction={systemAsAction}
                        botUseUserName={botUseUserName}
                        controlDeleteText={controlDeleteText}
                        pinnedMessage={pinnedMessage}
                        pinnedMessageBy={pinnedMessageBy}
                        highlightedMessage={startFromMessage}
                        onThreadActivate={onThreadActivate}
                        onPinMessage={onPinMessage}
                        onEditStart={handleMessageEditStart}
                        registerMessageCtrl={registerMessageCtrl}
                        canSendAsEmail={canSendAsEmail}
                        canLinkMessage={canLinkMessage}
                    >
                    {
                        messageGroups.map(group => <DiscussionDayGroup
                            key={group.day}
                            day={group.day}
                            noDivider={noDivider}
                            messages={group.items}
                        />)
                    }
                    </DiscussionProvider>
                </div>
            </div>
        </div>
    );   
}


const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();
    const getMessageNotificationsByAssociationId = makeGetMessageNotificationsByAssociationId();
    const getMessagesByTypeAndAssociationId = makeGetMessagesByTypeAndAssociationId();
    const getNonSystemMessagesByTypeAndAssociationId = makeGetNonSystemMessagesByTypeAndAssociationId();
    const getMessagesFetched = makeGetFetchedState();
    const getMessagesLoading = makeGetLoadingState();
    const getMessagesHasBefore = makeGetHasBefore();
    const getMessagesHasAfter = makeGetHasAfter();
    const getEarliestMessagesByTypeAndAssociationId = makeGetEarliestMessagesByTypeAndAssociationId();
    const getLastSelfMessage = makeGetLastSelfMessage();

    return ({ users, discussions, messages, notifications }, ownProps) => {
        const currentUserId = getCurrentUserId(users);
        return {
            currentUserId,
            messages: (ownProps.hideSystem ? getNonSystemMessagesByTypeAndAssociationId : getMessagesByTypeAndAssociationId)(messages.items, ownProps.type, ownProps.id, ownProps.excludeRemoved),
            earliestMessage: getEarliestMessagesByTypeAndAssociationId(messages.items, ownProps.type, ownProps.id, ownProps.excludeRemoved),
            lastSelfMessage: getLastSelfMessage(messages.items, ownProps.type, ownProps.id, currentUserId),
            fetched: getMessagesFetched(messages.fetchInfo, 'association', ownProps.id),
            loading: getMessagesLoading(messages.fetchInfo, 'association', ownProps.id),
            hasBefore: getMessagesHasBefore(messages.fetchInfo, 'association', ownProps.id),
            hasAfter: getMessagesHasAfter(messages.fetchInfo, 'association', ownProps.id),
            notifications: getMessageNotificationsByAssociationId(notifications.items, ownProps.id)
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation('chat')(DiscussionMessages));
